import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: string;
  serviceName?: string;
  serviceDescription?: string;
  jsonLd?: Record<string, unknown>;
}

const SEOHead = ({
  title,
  description,
  image = "/og-image.jpg",
  url,
  type = "website",
  serviceName,
  serviceDescription,
  jsonLd,
}: SEOHeadProps) => {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (property: string, content: string) => {
      let element = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.querySelector(`meta[name="${property}"]`) as HTMLMetaElement;
      }
      if (!element) {
        element = document.createElement("meta");
        if (property.startsWith("og:") || property.startsWith("twitter:")) {
          element.setAttribute("property", property);
        } else {
          element.setAttribute("name", property);
        }
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const absolute = (path: string) =>
      path.startsWith("http") ? path : `${origin}${path.startsWith("/") ? "" : "/"}${path}`;
    const canonicalUrl = url ? absolute(url) : (typeof window !== "undefined" ? window.location.href : "");

    // Basic meta tags
    updateMetaTag("description", description);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    // Open Graph tags
    updateMetaTag("og:title", title);
    updateMetaTag("og:description", description);
    updateMetaTag("og:type", type);
    updateMetaTag("og:image", absolute(image));
    updateMetaTag("og:url", canonicalUrl);

    // Twitter Card tags
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:title", title);
    updateMetaTag("twitter:description", description);
    updateMetaTag("twitter:image", absolute(image));

    // Schema.org structured data for services
    if (serviceName && serviceDescription) {
      let script = document.querySelector('script[type="application/ld+json"][data-service]');
      if (!script) {
        script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.setAttribute("data-service", "true");
        document.head.appendChild(script);
      }
      
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        name: serviceName,
        description: serviceDescription,
        provider: {
          "@type": "HealthAndBeautyBusiness",
          name: "ORIGINI Studio Olistico",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Via Roma, 334 - Montemerlo",
            addressLocality: "Cervarese Santa Croce",
            addressRegion: "PD",
            postalCode: "35030",
            addressCountry: "IT",
          },
          telephone: "+393461533000",
        },
      };
      
      script.textContent = JSON.stringify(structuredData);
    }

    // JSON-LD specifico della pagina (es. Person/ProfilePage)
    if (jsonLd) {
      let pageLd = document.querySelector('script[type="application/ld+json"][data-page-ld]');
      if (!pageLd) {
        pageLd = document.createElement("script");
        pageLd.setAttribute("type", "application/ld+json");
        pageLd.setAttribute("data-page-ld", "true");
        document.head.appendChild(pageLd);
      }
      pageLd.textContent = JSON.stringify(jsonLd);
    }
  }, [title, description, image, url, type, serviceName, serviceDescription, jsonLd]);

  return null;
};

export default SEOHead;
